import DOMPurify from "dompurify";

// Force all rendered links to open in a new tab safely (DOMPurify strips
// `target` by default). Registered once at module load.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Split HTML on existing <a>…</a> anchors and run `fn` only on the text between
// them, so we never linkify inside a link that's already there.
function replaceOutsideAnchors(html, fn) {
  return html
    .split(/(<a\b[^>]*>.*?<\/a>)/gi)
    .map((segment, index) => (index % 2 === 1 ? segment : fn(segment)))
    .join("");
}

// Turn bare URLs and email addresses into clickable links (mailto: for emails),
// leaving any already-linked content untouched.
function autoLink(html) {
  // Bare URLs → links.
  let out = replaceOutsideAnchors(html, (text) =>
    text.replace(
      /(https?:\/\/[^\s<>()]+)/g,
      (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
    ),
  );
  // Bare emails → mailto links (skip anything now inside the URL anchors above).
  out = replaceOutsideAnchors(out, (text) =>
    text.replace(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
      (email) => `<a href="mailto:${email}">${email}</a>`,
    ),
  );
  // Bare phone numbers → tel links. Match digit groups (optionally with +, spaces,
  // dashes, parens) and only accept them if they hold 10–15 digits, so we don't
  // linkify dates/ids.
  out = replaceOutsideAnchors(out, (text) =>
    text.replace(/(\+?\d[\d\s\-()]{7,}\d)/g, (candidate) => {
      const digits = candidate.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 15) return candidate;
      return `<a href="tel:${digits}">${candidate}</a>`;
    }),
  );
  return out;
}

// Minimal markdown: bold, italics, inline code, bullets, paragraphs.
export function renderMarkdownLite(text) {
  const escaped = escapeHtml(text || "");

  const withInline = autoLink(
    escaped
      // Markdown links [label](https://…) → a button-styled anchor.
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link-btn">$1</a>',
      ),
  )
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|\s)\*([^*\n]+)\*(?=\s|$|[.,!?])/g, "$1<em>$2</em>")
    .replace(/`([^`\n]+)`/g, "<code>$1</code>");

  const html = withInline
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block.split("\n").map((line) => {
        if (/^\s*[-*]\s+/.test(line)) {
          return `&bull; ${line.replace(/^\s*[-*]\s+/, "")}`;
        }
        return line;
      });
      return `<p>${lines.join("<br/>")}</p>`;
    })
    .join("");

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "code", "a"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    // DOMPurify blocks javascript:/data: URIs by default; allow web + mailto/tel.
    ALLOWED_URI_REGEXP: /^(https?:\/\/|mailto:|tel:)/i,
  });
}

export function formatTime(date) {
  try {
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}
